import { CatalogVisibility, ExpiryUnit, PackageProduct, ProductType } from "shared/types";
import { saveBlobAsFile } from "shared/utils/download";
import { now } from "shared/utils/dayjs";

// Tier IDs (`packages.id`) are internal autoincrement integers that the admin
// UI never renders, yet the Klien CSV importer identifies a package by exactly
// that value in its id_paket column. This export is the lookup table that
// closes the gap: import the catalogue first, download this, then fill id_paket
// against it (the migration converter leaves a paket_asal column to match on).

const EXPIRY_UNIT_LABELS: Record<string, string> = {
  [ExpiryUnit.Day]: "Hari",
  [ExpiryUnit.Month]: "Bulan",
  [ExpiryUnit.Year]: "Tahun",
};

const COLUMNS = [
  "id_paket",
  "nama_produk",
  "nama_tier",
  "tipe_produk",
  "total_sesi",
  "unlimited",
  "harga",
  "masa_berlaku",
  "satuan_berlaku",
  "kelas_tercakup",
  // What one booking of each covered class takes ("Reformer=2|Mat=1");
  // blank on a membership, which takes no credits. The lookup columns
  // before it (id_paket and friends) keep their positions for anyone who
  // pasted an earlier export; the two trailing columns are the ones that
  // moved.
  "kredit_per_kelas",
  "tampil_di_katalog_klien",
];

// Excel-compatible quoting: only when the value could break the row.
const escapeCell = (value: string) =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * One row per TIER (not per product) — id_paket is a tier-level value, and a
 * credit product with 4/8/12-session tiers needs three separate lookups.
 */
export const buildCatalogueCsv = (products: PackageProduct[]): string => {
  const rows = products.flatMap((product) =>
    product.tiers.map((tier) => {
      const unit = tier.expiry_unit ?? ExpiryUnit.Day;
      const value = tier.expiry_value ?? tier.expiry_duration_by_days;
      return [
        String(tier.id),
        product.name,
        tier.name,
        product.product_type,
        // Unlimited tiers carry the total_credits = 1 sentinel, which would
        // read as a one-session package; leave the cell blank instead.
        tier.unlimited ? "" : String(tier.total_credits),
        tier.unlimited ? "ya" : "",
        String(tier.price),
        String(value),
        EXPIRY_UNIT_LABELS[unit] ?? unit,
        product.class_type.map((klass) => klass.name).join(" | "),
        product.product_type === ProductType.Membership
          ? ""
          : product.class_type
              .map((klass) => `${klass.name}=${klass.credit_cost ?? 1}`)
              .join("|"),
        product.client_catalog_visibility === CatalogVisibility.Hidden
          ? "tidak"
          : "ya",
      ];
    }),
  );

  const lines = [
    COLUMNS.join(","),
    ...rows.map((cells) => cells.map(escapeCell).join(",")),
  ];
  // BOM so Excel reads it as UTF-8 rather than mangling accented names.
  return "﻿" + lines.join("\r\n") + "\r\n";
};

export const downloadCatalogueCsv = (products: PackageProduct[]) =>
  saveBlobAsFile(
    new Blob([buildCatalogueCsv(products)], { type: "text/csv;charset=utf-8" }),
    `spott-katalog-${now().format("YYYYMMDD")}.csv`,
  );
